import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { DRAGGABLE, DROPPABLE, useDndContext } from "./DragDropProvider";
import { useFlipAnimation } from "./flip";

const DroppableContext = createContext(null);

export function useDroppable() {
  const ctx = useContext(DroppableContext);
  if (!ctx) throw new Error("<Draggable> must be used inside <Droppable>");
  return ctx;
}

/**
 * <Droppable droppableId="list-1" direction="vertical">
 *   {children}  or  {({ isDraggingOver }) => children}
 * </Droppable>
 *
 * Any other props (className, style, onClick, ...) go to the wrapper element.
 * as: wrapper element to render (default "div"), e.g. "tbody" for table rows.
 * animationDuration: ms for items sliding into place (0 disables).
 */
export function Droppable({
  droppableId,
  direction = "vertical",
  animationDuration = 200,
  as: Element = "div",
  style,
  children,
  className,
  ...rest
}) {
  const ref = useRef(null);
  const { instanceId } = useDndContext();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  useFlipAnimation(ref, animationDuration);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      canDrop: ({ source }) =>
        source.data.type === DRAGGABLE && source.data.instanceId === instanceId,
      getData: () => ({ type: DROPPABLE, droppableId }),
      getIsSticky: () => true,
      onDragStart: () => setIsDraggingOver(true),
      onDragEnter: () => setIsDraggingOver(true),
      onDragLeave: () => setIsDraggingOver(false),
      onDrop: () => setIsDraggingOver(false),
    });
  }, [droppableId, instanceId]);

  return (
    <DroppableContext.Provider value={{ droppableId, direction }}>
      <Element ref={ref} data-dragging-over={isDraggingOver || undefined} {...rest} className={className ?? ""} style={{
        ...style,
        background: isDraggingOver ? 'lightblue' : (style && style.background) || ''
      }}>
        {typeof children === "function"
          ? children({ isDraggingOver })
          : children}
      </Element>
    </DroppableContext.Provider>
  );
}
